/**
 * Lottie Icon Configuration
 * Manages scaling and normalization for different Lottie icons
 */

import { LottieAnimationName } from './lottie-manager'

/**
 * Icon dimensions and scaling configuration
 * Each icon has its actual dimensions and a recommended scale factor
 * to normalize it to a standard size (16x16)
 */
export interface IconConfig {
    actualWidth: number
    actualHeight: number
    baseScale: number       // Base scale to normalize to 16x16
    offsetX?: number        // Optional horizontal offset for centering
    offsetY?: number        // Optional vertical offset for centering
}

/**
 * Configuration for each icon based on actual measurements
 * Target: Normalize all icons to appear as 16x16 within a 32x32 container
 */
export const iconConfigurations: Record<LottieAnimationName, IconConfig> = {
    'send': {
        actualWidth: 21.28,
        actualHeight: 21.21,
        baseScale: 0.75,    // 16 / 21.28 ≈ 0.75
        offsetX: 0,
        offsetY: 0
    },
    'paperclip': {
        actualWidth: 14.92,
        actualHeight: 15.02,
        baseScale: 1.07,    // 16 / 14.92 ≈ 1.07
        offsetX: 0,
        offsetY: 0
    },
    'sidebar': {
        actualWidth: 20.84,
        actualHeight: 20.84,
        baseScale: 0.77,    // 16 / 20.84 ≈ 0.77
        offsetX: 0,
        offsetY: 0
    },
    'bookmark': {
        actualWidth: 16,     // Default normalized size
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'bookmark-add': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'bookmark-remove': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'error404': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'file': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'validate': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'scroll': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'chevron-down': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'edit': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'trash': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'menu': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'lifebuoy': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'feedback': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
    'support': {
        actualWidth: 16,
        actualHeight: 16,
        baseScale: 1,
        offsetX: 0,
        offsetY: 0
    },
}

/**
 * Available scale multipliers for testing and display
 */
export const scaleMultipliers = [
    1.00, 1.05, 1.10, 1.15, 1.20,
    1.25, 1.30, 1.35, 1.40, 1.45, 1.50, 1.55, 1.60, 1.65, 1.70, 1.75, 1.80, 1.85, 1.90, 1.95, 2.00
]

/**
 * Calculate the final scale for an icon
 * @param iconName - The name of the icon
 * @param multiplier - The scale multiplier (1.0 to 1.5)
 * @returns The final scale value
 */
export function calculateIconScale(
    iconName: LottieAnimationName, 
    multiplier: number = 1.0
): number {
    const config = iconConfigurations[iconName]
    if (!config) {
        console.warn(`No configuration found for icon: ${iconName}`)
        return multiplier
    }
    
    return config.baseScale * multiplier
}

/**
 * Get normalized dimensions for an icon
 * @param iconName - The name of the icon
 * @param targetSize - Target size in pixels (default 16)
 * @param multiplier - Optional scale multiplier
 * @returns Object with width, height, and scale
 */
export function getNormalizedDimensions(
    iconName: LottieAnimationName,
    targetSize: number = 16,
    multiplier: number = 1.0
) {
    const config = iconConfigurations[iconName]
    if (!config) {
        return {
            width: targetSize,
            height: targetSize,
            scale: multiplier
        }
    }
    
    const scale = calculateIconScale(iconName, multiplier)
    const normalizedSize = targetSize * multiplier
    
    return {
        width: normalizedSize,
        height: normalizedSize,
        scale,
        offsetX: config.offsetX || 0,
        offsetY: config.offsetY || 0
    }
}

/**
 * Get container styles for centering and containing the icon
 * @param containerSize - The size of the container (default 32)
 * @param iconSize - The size of the icon (default 16)
 * @returns CSS styles object
 */
export function getContainerStyles(
    containerSize: number = 32,
    iconSize: number = 16
) {
    return {
        width: `${containerSize}px`,
        height: `${containerSize}px`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative' as const,
        overflow: 'hidden'
    }
}
